import { Role } from '@ankor-io/common/types/account'

// Type definitions based on isPrivilegedRole function
// to add another privileged role use the | operator for instance Role.ADMIN | Role.APPROVER
export type PrivilegedRole = Role.ADMIN | Role.OWNER
export type ApproverOrHigherRole = Role.APPROVER | Role.ADMIN | Role.OWNER
// An Editor is considered a user that has a role that allows them to make edits on an object that exists somewhere in the hierarchy of the connected account.
// Non privileged roles must have access to the connected account to be considered a connected account editor.
// This nomenclature does not surface to the UI, it is used internally and in agreement with Product to identify a specific role + permission in the system
export type ConnectedAccountEditorRole = Role.APPROVER | Role.SPENDER | Role.ADMIN | Role.OWNER

export type NonPrivilegedRole = Exclude<Role, PrivilegedRole>
export type NotApproverOrHigherRole = Exclude<Role, ApproverOrHigherRole>

/**
 * Returns the highest role from a list of roles.
 * The enum is ordered from highest (ADMIN) to lowest (VIEWER).
 * @param roles - Array of roles to evaluate
 * @returns The highest role, or null if the array is empty
 */
export function getHighestRole(roles: []): null
export function getHighestRole(roles: [Role, ...Role[]]): Role
export function getHighestRole(roles: Role[]): Role | null
export function getHighestRole(roles: Role[]): Role | null {
  if (roles.length === 0) {
    return null
  }

  // Since the enum is ordered from highest to lowest,
  // we can find the highest role by finding the one with the lowest index
  const roleValues = Object.values(Role)

  return roles.reduce((highestRole, currentRole) => {
    const currentIndex = roleValues.indexOf(currentRole)
    const highestIndex = roleValues.indexOf(highestRole)
    return currentIndex < highestIndex ? currentRole : highestRole
  })
}

/**
 * Returns true if the role is a privileged role. Privileged roles get usually more access to the system.
 * For instance they get access to all the connected accounts without requiring explicit permission.
 * Overload this function defining all the return types so we can narrow down the return type at compile time.
 * @param role - The role to evaluate
 * @returns True if the role is a privileged role, false otherwise
 */
export function isPrivilegedRole(role: PrivilegedRole): true
export function isPrivilegedRole(role: NonPrivilegedRole): false
export function isPrivilegedRole(role: Role): role is PrivilegedRole
export function isPrivilegedRole(role: Role): role is PrivilegedRole {
  return role === Role.OWNER || role === Role.ADMIN
}

/**
 * Returns true if the role is an approver or higher role.
 * @param role - The role to evaluate
 * @returns True if the role is an approver or higher role, false otherwise
 */
export function isApproverOrHigherRole(role: ApproverOrHigherRole): true
export function isApproverOrHigherRole(role: NotApproverOrHigherRole): false
export function isApproverOrHigherRole(role: Role): role is ApproverOrHigherRole
export function isApproverOrHigherRole(role: Role): role is ApproverOrHigherRole {
  return ROLE(role).isEqualOrHigherThan(Role.APPROVER)
}

export function minimumRequiredRole(userRole: Role, requiredRole: Role) {
  return ROLE(userRole).isEqualOrHigherThan(requiredRole)
}

export const ROLE = (role: Role) => {
  return {
    isPrivileged: isPrivilegedRole(role),
    isNonPrivileged: !isPrivilegedRole(role),
    isEqualOrLowerThan: (otherRole: Role) => {
      const roleValues = Object.values(Role)
      return roleValues.indexOf(role) >= roleValues.indexOf(otherRole)
    },
    isEqualOrHigherThan: (otherRole: Role) => {
      const roleValues = Object.values(Role)
      return roleValues.indexOf(role) <= roleValues.indexOf(otherRole)
    },
    isEqualTo: (otherRole: Role) => {
      return role === otherRole
    },
  }
}
